import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const RMStatusReport = () => {
  const [filters, setFilters] = useState({
    steel_code: '',
    location: '',
    status: '',
    min_quantity: '',
    max_quantity: ''
  });

  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAuth();

  const { data: reportData, isLoading, error, refetch } = useQuery(
    ['rmStatusReport', filters],
    async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      // This would call the existing RM status API
      const response = await api.get(`/raw-material/status?${params}`);
      return response.data;
    },
    {
      onError: (error) => {
        enqueueSnackbar(`Error fetching RM status report: ${error.message}`, { variant: 'error' });
      }
    }
  );

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    enqueueSnackbar('Download functionality will be implemented', { variant: 'info' });
  };

  const handleGenerateReport = () => {
    refetch();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'available': return 'success';
      case 'reserved': return 'warning';
      case 'issued': return 'info';
      case 'consumed': return 'default';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStockLevel = (balance, totalReceived) => {
    if (!totalReceived || totalReceived === 0) return 0;
    return (balance / totalReceived) * 100;
  };

  const getStockLevelColor = (level) => {
    if (level < 20) return 'error';
    if (level < 50) return 'warning';
    return 'success';
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading RM status report: {error.message}
        </Alert>
      </Box>
    );
  }

  const statusData = reportData?.data?.status || [];
  const summary = reportData?.data?.summary || {};

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <InventoryIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" gutterBottom color="primary">
          Raw Material Status Report
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Report Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Steel Code"
              value={filters.steel_code}
              onChange={(e) => setFilters({ ...filters, steel_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Location"
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="reserved">Reserved</MenuItem>
                <MenuItem value="issued">Issued</MenuItem>
                <MenuItem value="consumed">Consumed</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Min Quantity"
              type="number"
              value={filters.min_quantity}
              onChange={(e) => setFilters({ ...filters, min_quantity: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Max Quantity"
              type="number"
              value={filters.max_quantity}
              onChange={(e) => setFilters({ ...filters, max_quantity: e.target.value })}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleGenerateReport}
              startIcon={<SearchIcon />}
            >
              Generate Report
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box mb={3} display="flex" gap={2}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Export to Excel
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="primary">
                    {summary.total_materials || statusData.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Materials
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="h5" color="success.main">
                      {summary.total_quantity_received ? parseFloat(summary.total_quantity_received).toFixed(1) : '0.0'} T
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    Total Received
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <TrendingDownIcon color="warning" sx={{ mr: 1 }} />
                    <Typography variant="h5" color="warning.main">
                      {summary.total_quantity_issued ? parseFloat(summary.total_quantity_issued).toFixed(1) : '0.0'} T
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    Total Issued
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="info.main">
                    {summary.total_balance ? parseFloat(summary.total_balance).toFixed(1) : '0.0'} T
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Current Balance
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* RM Status Table */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                Raw Material Inventory Status
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Current stock levels and material positions
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Steel Code</strong></TableCell>
                    <TableCell><strong>Steel Name</strong></TableCell>
                    <TableCell><strong>Heat Number</strong></TableCell>
                    <TableCell align="right"><strong>Received (T)</strong></TableCell>
                    <TableCell align="right"><strong>Issued (T)</strong></TableCell>
                    <TableCell align="right"><strong>Balance (T)</strong></TableCell>
                    <TableCell align="center"><strong>Stock Level</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statusData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No raw material status data found for the selected criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    statusData.map((record, index) => {
                      const stockLevel = getStockLevel(record.balance, record.total_received);
                      return (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Chip 
                              label={record.steel_code} 
                              color="primary" 
                              variant="outlined" 
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{record.steel_name || '-'}</TableCell>
                          <TableCell>{record.heat_no}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="success.main">
                              {parseFloat(record.total_received || 0).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="warning.main">
                              {parseFloat(record.total_issued || 0).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              color={parseFloat(record.balance || 0) > 0 ? "info.main" : "error.main"}
                              fontWeight="bold"
                            >
                              {parseFloat(record.balance || 0).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 100 }}>
                              <Box sx={{ width: '100%', mr: 1 }}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={Math.min(stockLevel, 100)} 
                                  color={getStockLevelColor(stockLevel)}
                                  sx={{ height: 8, borderRadius: 4 }}
                                />
                              </Box>
                              <Typography variant="caption" color="textSecondary">
                                {stockLevel.toFixed(0)}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={parseFloat(record.balance || 0) > 0 ? "Available" : "Consumed"} 
                              color={parseFloat(record.balance || 0) > 0 ? "success" : "default"} 
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default RMStatusReport;